import { mediaQueryLarge } from '@theme/utilities';

const SELECTOR = '[data-resource-list-mobile-carousel]';
const TEMPLATE_SELECTOR = 'template[data-resource-list-carousel-template]';
const ACTIVE_CLASS = 'resource-list--carousel-active';

/** @type {Promise<void> | undefined} */
let slideshowReady;

function ensureSlideshow() {
  if (!slideshowReady) {
    slideshowReady = import('@theme/slideshow')
      .then(() => customElements.whenDefined('slideshow-component'))
      .catch((error) => {
        console.error('Failed to load slideshow component for resource list carousel:', error);
        slideshowReady = undefined;
      });
  }

  return slideshowReady || Promise.resolve();
}

class MobileCarouselController {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
    /** @type {HTMLTemplateElement | null} */
    this.template = container.querySelector(TEMPLATE_SELECTOR);
    /** @type {AbortController | null} */
    this.abortController = null;
    /** @type {HTMLElement | null} */
    this.carouselRoot = null;
    /** @type {HTMLElement[]} */
    this.itemOrder = [];
  }

  init() {
    if (!this.template) return false;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    mediaQueryLarge.addEventListener('change', this.handleMediaChange, { signal });
    this.handleMediaChange(mediaQueryLarge);

    return true;
  }

  destroy() {
    this.disableCarousel();
    this.abortController?.abort();
    this.abortController = null;
  }

  /** @param {MediaQueryList | MediaQueryListEvent} query */
  handleMediaChange = (query) => {
    const matches = typeof query.matches === 'boolean' ? query.matches : mediaQueryLarge.matches;
    if (matches) {
      this.disableCarousel();
    } else {
      void this.enableCarousel();
    }
  };

  async enableCarousel() {
    if (this.carouselRoot || !this.template) return;

    const items = Array.from(this.container.querySelectorAll(':scope > .resource-list__item'));
    if (!items.length) return;

    this.itemOrder = items;

    await ensureSlideshow();
    if (!this.container.isConnected) return;

    const clone = this.template.content.firstElementChild?.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return;

    const slideshow = clone.querySelector('slideshow-component');
    const slidesRoot = clone.querySelector('slideshow-slides');
    if (!(slideshow instanceof HTMLElement) || !(slidesRoot instanceof HTMLElement)) {
      return;
    }

    const fragment = document.createDocumentFragment();

    items.forEach((item, index) => {
      const slide = document.createElement('slideshow-slide');
      slide.classList.add('resource-list__slide');
      slide.setAttribute('ref', 'slides[]');
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      slide.style.setProperty('view-timeline-name', `--slide-${index + 1}`);
      slide.style.setProperty('--product-media-fit', 'cover');
      slide.appendChild(item);
      fragment.appendChild(slide);
    });

    slidesRoot.appendChild(fragment);
    slideshow.setAttribute('slide-count', String(items.length));
    slideshow.setAttribute('initial-slide', '0');

    this.carouselRoot = clone;
    this.container.insertBefore(clone, this.template);
    this.container.classList.add(ACTIVE_CLASS);

    if (typeof slideshow.updatedCallback === 'function') {
      slideshow.updatedCallback();
    }
  }

  disableCarousel() {
    if (!this.carouselRoot || !this.template) return;

    const fragment = document.createDocumentFragment();

    for (const item of this.itemOrder) {
      if (!item.parentElement) continue;
      fragment.appendChild(item);
    }

    this.container.insertBefore(fragment, this.template);

    this.carouselRoot.remove();
    this.carouselRoot = null;
    this.container.classList.remove(ACTIVE_CLASS);
    this.itemOrder = [];
  }
}

/** @type {WeakMap<HTMLElement, MobileCarouselController>} */
const instances = new WeakMap();

function createInstance(container) {
  if (!(container instanceof HTMLElement)) return;
  if (instances.has(container)) return;

  const controller = new MobileCarouselController(container);
  if (controller.init()) {
    instances.set(container, controller);
  }
}

function destroyInstance(container) {
  const controller = instances.get(container);
  if (!controller) return;
  controller.destroy();
  instances.delete(container);
}

function initWithin(root) {
  if (!(root instanceof Element || root instanceof Document || root instanceof DocumentFragment)) return;

  if (root instanceof Element && root.matches(SELECTOR)) {
    createInstance(root);
  }

  const targets = root.querySelectorAll?.(SELECTOR) ?? [];
  targets.forEach((element) => createInstance(element));
}

function destroyWithin(root) {
  if (!(root instanceof Element || root instanceof DocumentFragment)) return;

  const targets = root.matches?.(SELECTOR)
    ? [root, ...root.querySelectorAll(SELECTOR)]
    : root.querySelectorAll?.(SELECTOR) ?? [];

  targets.forEach((element) => destroyInstance(/** @type {HTMLElement} */ (element)));
}

initWithin(document);

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  initWithin(target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  destroyWithin(target);
});

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement || node instanceof DocumentFragment) {
        initWithin(node);
      }
    });

    mutation.removedNodes.forEach((node) => {
      if (node instanceof HTMLElement || node instanceof DocumentFragment) {
        destroyWithin(node);
      }
    });
  }
});

observer.observe(document.documentElement, { childList: true, subtree: true });
